/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CheckCircle,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { services } from '../../services';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';

type KycDocumentType = 'PAN' | 'DRIVING_LICENCE' | 'PASSPORT';
type KycDocumentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

interface KycDocument {
  _id: string;
  documentType: KycDocumentType;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  status: KycDocumentStatus;
  rejectionReason?: string | null;
  reviewedAt?: string | null;
  presignedUrl?: string | null;
}

interface KycUserInfo {
  _id: string;
  name?: string;
  email?: string;
  walletAddress?: string;
  isKycVerified?: boolean;
  status?: string;
}

interface Props {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewComplete?: () => void;
}

const TYPE_LABELS: Record<KycDocumentType, string> = {
  PAN: 'PAN Card',
  DRIVING_LICENCE: 'Driving Licence',
  PASSPORT: 'Passport',
};

export default function KycReviewDialog({ userId, open, onOpenChange, onReviewComplete }: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<KycUserInfo | null>(null);
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [actingDocId, setActingDocId] = useState<string | null>(null);
  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchDocuments = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const response: any = await services.get(`admin/users/${userId}/kyc-documents`);
      if (response?.status === 200) {
        setUserInfo(response.data?.data?.user ?? null);
        setDocuments(response.data?.data?.documents ?? []);
      } else {
        setFetchError(response?.data?.message || `Unexpected response status ${response?.status}`);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to load KYC documents';
      setFetchError(message);
      toast({ description: message, variant: 'destructive' });
      // eslint-disable-next-line no-console
      console.error('[KycReviewDialog] fetch error', error);
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    if (open) {
      setRejectingDocId(null);
      setRejectionReason('');
      setUserInfo(null);
      setDocuments([]);
      fetchDocuments();
    }
  }, [open, fetchDocuments]);

  const reviewDocument = async (
    docId: string,
    status: 'APPROVED' | 'REJECTED',
    reason?: string
  ) => {
    setActingDocId(docId);
    try {
      const body: any = { status };
      if (status === 'REJECTED') body.rejectionReason = reason;
      const response: any = await services.patch(
        `admin/users/${userId}/kyc-documents/${docId}`,
        body
      );
      if (response?.status === 200) {
        toast({
          description: `Document ${status === 'APPROVED' ? 'approved' : 'rejected'} successfully`,
        });
        await fetchDocuments();
        onReviewComplete?.();
      }
    } catch (error: any) {
      toast({
        description: error?.data?.message || 'Failed to update document',
        variant: 'destructive',
      });
    } finally {
      setActingDocId(null);
      setRejectingDocId(null);
      setRejectionReason('');
    }
  };

  const handleReject = (docId: string) => {
    if (!rejectionReason.trim()) {
      toast({ description: 'Please provide a rejection reason', variant: 'destructive' });
      return;
    }
    reviewDocument(docId, 'REJECTED', rejectionReason.trim());
  };

  const statusBadge = (status: KycDocumentStatus) => {
    if (status === 'APPROVED')
      return <Badge className='bg-success/20 text-success hover:bg-success/20'>Approved</Badge>;
    if (status === 'REJECTED')
      return (
        <Badge className='bg-destructive/20 text-destructive hover:bg-destructive/20'>
          Rejected
        </Badge>
      );
    return <Badge className='bg-warning/20 text-warning hover:bg-warning/20'>Pending</Badge>;
  };

  const renderPreview = (doc: KycDocument) => {
    if (!doc.presignedUrl) {
      return (
        <div className='flex items-center justify-center h-40 bg-muted/30 rounded-lg text-sm text-muted-foreground'>
          Preview unavailable
        </div>
      );
    }
    if (doc.mimeType?.startsWith('image/')) {
      return (
        <a href={doc.presignedUrl} target='_blank' rel='noopener noreferrer' className='block'>
          <img
            src={doc.presignedUrl}
            alt={doc.originalName}
            className='max-h-64 w-full object-contain rounded-lg bg-muted/30'
          />
        </a>
      );
    }
    if (doc.mimeType === 'application/pdf') {
      return (
        <iframe
          title={doc.originalName}
          src={doc.presignedUrl}
          className='w-full h-64 rounded-lg bg-muted/30'
        />
      );
    }
    return (
      <div className='flex items-center justify-center h-40 bg-muted/30 rounded-lg text-sm text-muted-foreground'>
        No inline preview
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>KYC Documents Review</DialogTitle>
          <DialogDescription>
            {userInfo
              ? `${userInfo.name || 'User'} · ${userInfo.email || userInfo.walletAddress || ''}`
              : 'Loading user details...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : fetchError ? (
          <div className='rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive'>
            <p className='font-medium mb-1'>Failed to load documents</p>
            <p className='text-xs'>{fetchError}</p>
          </div>
        ) : documents.length === 0 ? (
          <p className='text-center text-muted-foreground py-8'>
            This user has not submitted any KYC documents yet.
          </p>
        ) : (
          <div className='space-y-5'>
            {documents.map((doc) => {
              const isActing = actingDocId === doc._id;
              const isRejecting = rejectingDocId === doc._id;
              return (
                <div key={doc._id} className='rounded-lg border border-border p-4 space-y-3'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-center gap-2'>
                      {doc.mimeType?.startsWith('image/') ? (
                        <ImageIcon className='w-5 h-5 text-info' />
                      ) : (
                        <FileText className='w-5 h-5 text-info' />
                      )}
                      <div>
                        <p className='font-medium text-foreground'>
                          {TYPE_LABELS[doc.documentType]}
                        </p>
                        <p className='text-xs text-muted-foreground'>
                          {doc.originalName} · {(doc.size / 1024).toFixed(1)} KB ·{' '}
                          {new Date(doc.uploadedAt).toLocaleString()}
                        </p>
                        {doc.status === 'REJECTED' && doc.rejectionReason && (
                          <p className='text-xs text-destructive mt-1'>
                            Rejected: {doc.rejectionReason}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      {statusBadge(doc.status)}
                    </div>
                  </div>

                  {renderPreview(doc)}

                  <div className='flex justify-end'>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={!doc.presignedUrl}
                      onClick={() => {
                        if (doc.presignedUrl) {
                          window.open(doc.presignedUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <ExternalLink className='w-4 h-4 mr-2' />
                      Open Document
                    </Button>
                  </div>

                  {doc.status === 'PENDING' && (
                    <>
                      {isRejecting ? (
                        <div className='space-y-2'>
                          <Textarea
                            placeholder='Enter reason for rejection (required)'
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={3}
                          />
                          <div className='flex gap-2 justify-end'>
                            <Button
                              size='sm'
                              variant='ghost'
                              onClick={() => {
                                setRejectingDocId(null);
                                setRejectionReason('');
                              }}
                              disabled={isActing}
                            >
                              Cancel
                            </Button>
                            <Button
                              size='sm'
                              variant='destructive'
                              onClick={() => handleReject(doc._id)}
                              disabled={isActing || !rejectionReason.trim()}
                            >
                              {isActing ? (
                                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                              ) : (
                                <XCircle className='w-4 h-4 mr-2' />
                              )}
                              Confirm Reject
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className='flex gap-2 justify-end'>
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => setRejectingDocId(doc._id)}
                            disabled={isActing}
                          >
                            <XCircle className='w-4 h-4 mr-2' />
                            Reject
                          </Button>
                          <Button
                            size='sm'
                            onClick={() => reviewDocument(doc._id, 'APPROVED')}
                            disabled={isActing}
                            className='bg-success hover:bg-success/90'
                          >
                            {isActing ? (
                              <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                            ) : (
                              <CheckCircle className='w-4 h-4 mr-2' />
                            )}
                            Approve
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
